import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';

import { adminErrorMessage, adminFetch } from './api';
import type { AdminPlan } from './types';
import { formatPlanPrice } from '../subscription/api';
import styles from './admin.module.css';

interface Draft {
  id: string;
  name: string;
  priceYuan: string;
  durationDays: number;
  summary: string;
  tags: string;
  description: string;
  autoText: boolean;
  autoImage: boolean;
  autoTts: boolean;
  quotaEnabled: boolean;
  textTokenQuota: number;
  imageQuota: number;
  ttsCharQuota: number;
  listed: boolean;
  sortOrder: number;
}

function emptyDraft(): Draft {
  return {
    id: '',
    name: '',
    priceYuan: '10.00',
    durationDays: 30,
    summary: '',
    tags: '',
    description: '',
    autoText: true,
    autoImage: false,
    autoTts: false,
    quotaEnabled: false,
    textTokenQuota: 1_000_000,
    imageQuota: 100,
    ttsCharQuota: 100_000,
    listed: false,
    sortOrder: 0,
  };
}

function draftOf(plan: AdminPlan): Draft {
  return {
    id: plan.id,
    name: plan.name,
    priceYuan: (plan.priceCents / 100).toFixed(2),
    durationDays: plan.durationDays,
    summary: plan.summary,
    tags: plan.tags.join('，'),
    description: plan.description,
    autoText: plan.capabilities.autoText,
    autoImage: plan.capabilities.autoImage,
    autoTts: plan.capabilities.autoTts,
    quotaEnabled: plan.quotaEnabled,
    textTokenQuota: plan.textTokenQuota,
    imageQuota: plan.imageQuota,
    ttsCharQuota: plan.ttsCharQuota,
    listed: plan.listed,
    sortOrder: plan.sortOrder,
  };
}

function payload(draft: Draft, creating: boolean): Record<string, unknown> {
  const priceCents = Math.round(Number(draft.priceYuan) * 100);
  if (!Number.isSafeInteger(priceCents) || priceCents <= 0) throw new Error('价格必须大于 0');
  if (!draft.name.trim()) throw new Error('套餐名称不能为空');
  if (draft.quotaEnabled && draft.textTokenQuota <= 0 && draft.imageQuota <= 0 && draft.ttsCharQuota <= 0) {
    throw new Error('开启额度后至少填写一项正数额度');
  }
  return {
    ...(creating ? { id: draft.id.trim() } : {}),
    name: draft.name.trim(),
    priceCents,
    durationDays: draft.durationDays,
    summary: draft.summary.trim(),
    tags: draft.tags.split(/[，,\n]/).map((tag) => tag.trim()).filter(Boolean),
    description: draft.description.trim(),
    capabilities: { autoText: draft.autoText, autoImage: draft.autoImage, autoTts: draft.autoTts },
    quotaEnabled: draft.quotaEnabled,
    textTokenQuota: draft.textTokenQuota,
    imageQuota: draft.imageQuota,
    ttsCharQuota: draft.ttsCharQuota,
    listed: draft.listed,
    sortOrder: draft.sortOrder,
  };
}

export function PlansPanel(): ReactElement {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await adminFetch<{ plans: AdminPlan[] }>('/admin/plans');
      setPlans(result.plans);
    } catch (failure) {
      setError(adminErrorMessage(failure));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!draft || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const body = payload(draft, creating);
      if (creating) await adminFetch('/admin/plans', { method: 'POST', body });
      else await adminFetch(`/admin/plans/${encodeURIComponent(draft.id)}`, { method: 'PATCH', body: payload(draft, false) });
      setNotice(creating ? '套餐已创建。' : '套餐已保存。');
      setDraft(null);
      setCreating(false);
      await load();
    } catch (failure) {
      setError(failure instanceof Error && failure.message.startsWith('价格') ? failure.message : adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const mutate = async (plan: AdminPlan, body: Record<string, unknown>, message: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminFetch(`/admin/plans/${encodeURIComponent(plan.id)}`, { method: 'PATCH', body });
      setNotice(message);
      await load();
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (plan: AdminPlan): Promise<void> => {
    if (busy || !window.confirm(`归档套餐「${plan.name}」？已购买用户的有效权益会保留至到期，但套餐不再可购买。`)) return;
    setBusy(true);
    setError('');
    try {
      await adminFetch(`/admin/plans/${encodeURIComponent(plan.id)}`, { method: 'DELETE' });
      setNotice('套餐已归档。');
      await load();
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  if (draft) {
    return (
      <form className={styles.planEditor} onSubmit={(event) => void save(event)}>
        <div className={styles.toolbar}>
          <h3>{creating ? '新建套餐' : `编辑套餐 · ${draft.name}`}</h3>
          <div className={styles.actions}>
            <button type="button" disabled={busy} onClick={() => { setDraft(null); setCreating(false); }}>返回列表</button>
            <button type="submit" className={styles.primary} disabled={busy}>{busy ? '保存中…' : '保存套餐'}</button>
          </div>
        </div>
        <div className={styles.fields}>
          {creating ? <label className={styles.field}><span>套餐 ID（小写字母、数字、- 或 _）</span><input required pattern="[a-z0-9][a-z0-9_-]{1,63}" value={draft.id} onChange={(event) => setDraft({ ...draft, id: event.target.value })} /></label> : null}
          <label className={styles.field}><span>套餐名称</span><input required maxLength={80} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
          <label className={styles.field}><span>价格（元）</span><input required type="number" min="0.01" step="0.01" value={draft.priceYuan} onChange={(event) => setDraft({ ...draft, priceYuan: event.target.value })} /></label>
          <label className={styles.field}><span>有效天数</span><input required type="number" min={1} max={3650} step={1} value={draft.durationDays} onChange={(event) => setDraft({ ...draft, durationDays: event.target.valueAsNumber })} /></label>
          <label className={styles.field}><span>排序（越小越靠前）</span><input type="number" step={1} value={draft.sortOrder} onChange={(event) => setDraft({ ...draft, sortOrder: event.target.valueAsNumber })} /></label>
          <label className={styles.field}><span>套餐简述</span><input maxLength={240} value={draft.summary} onChange={(event) => setDraft({ ...draft, summary: event.target.value })} /></label>
          <label className={styles.field}><span>套餐标签（逗号分隔）</span><input value={draft.tags} onChange={(event) => setDraft({ ...draft, tags: event.target.value })} /></label>
          <label className={styles.field}><span>套餐描述</span><textarea rows={5} maxLength={10000} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
        </div>
        <fieldset className={styles.fields}>
          <legend>功能权限</legend>
          <label className={styles.check}><input type="checkbox" checked={draft.autoText} onChange={(event) => setDraft({ ...draft, autoText: event.target.checked })} />允许自动生文（冒泡 / 朋友圈）</label>
          <label className={styles.check}><input type="checkbox" checked={draft.autoImage} onChange={(event) => setDraft({ ...draft, autoImage: event.target.checked })} />允许自动生图</label>
          <label className={styles.check}><input type="checkbox" checked={draft.autoTts} onChange={(event) => setDraft({ ...draft, autoTts: event.target.checked })} />允许自动语音</label>
          <label className={styles.check}><input type="checkbox" checked={draft.listed} onChange={(event) => setDraft({ ...draft, listed: event.target.checked })} />上架销售</label>
        </fieldset>
        <fieldset className={styles.fields}>
          <legend>套餐内月度额度</legend>
          <label className={styles.check}><input type="checkbox" checked={draft.quotaEnabled} onChange={(event) => setDraft({ ...draft, quotaEnabled: event.target.checked })} />启用套餐额度（关闭则官方调用不限量）</label>
          <label className={styles.field}><span>文字 token / 月</span><input type="number" min={0} step={1} disabled={!draft.quotaEnabled} value={draft.textTokenQuota} onChange={(event) => setDraft({ ...draft, textTokenQuota: event.target.valueAsNumber })} /></label>
          <label className={styles.field}><span>生图张数 / 月</span><input type="number" min={0} step={1} disabled={!draft.quotaEnabled} value={draft.imageQuota} onChange={(event) => setDraft({ ...draft, imageQuota: event.target.valueAsNumber })} /></label>
          <label className={styles.field}><span>TTS 字符 / 月</span><input type="number" min={0} step={1} disabled={!draft.quotaEnabled} value={draft.ttsCharQuota} onChange={(event) => setDraft({ ...draft, ttsCharQuota: event.target.valueAsNumber })} /></label>
        </fieldset>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
      </form>
    );
  }

  return (
    <div className={styles.planPanel}>
      <div className={styles.toolbar}>
        <h3>官方套餐</h3>
        <div className={styles.actions}>
          <button type="button" disabled={busy} onClick={() => void load()}>刷新</button>
          <button type="button" className={styles.primary} disabled={busy} onClick={() => { setCreating(true); setDraft(emptyDraft()); setError(''); setNotice(''); }}>新建套餐</button>
        </div>
      </div>
      <p className={styles.scopeHint}>删除采用归档：已购买用户权益保留至到期，但不再上架。额度按北京时间自然月重置。</p>
      {plans.length === 0 ? <p className={styles.empty}>暂无套餐。</p> : (
        <div className={styles.planList}>
          {plans.map((plan) => (
            <article key={plan.id} className={styles.planRow}>
              <div>
                <strong>{plan.name}</strong>
                <small>{plan.id} · {formatPlanPrice(plan.priceCents)} · {plan.durationDays} 天 · {plan.listed ? '已上架' : '未上架'}{plan.archivedAt ? ' · 已归档' : ''}</small>
                <p>{plan.summary || plan.description || '暂无简述'}</p>
                <div className={styles.planBadges}>
                  {plan.capabilities.autoText ? <span>自动生文</span> : null}
                  {plan.capabilities.autoImage ? <span>自动生图</span> : null}
                  {plan.capabilities.autoTts ? <span>自动语音</span> : null}
                  {plan.quotaEnabled ? <span>月度额度</span> : <span>不限额度</span>}
                </div>
              </div>
              {!plan.archivedAt ? (
                <div className={styles.rowActions}>
                  <button type="button" disabled={busy} onClick={() => { setCreating(false); setDraft(draftOf(plan)); setError(''); setNotice(''); }}>编辑</button>
                  <button type="button" disabled={busy} onClick={() => void mutate(plan, { listed: !plan.listed }, plan.listed ? '套餐已下架。' : '套餐已上架。')}>{plan.listed ? '下架' : '上架'}</button>
                  <button type="button" className={styles.danger} disabled={busy} onClick={() => void archive(plan)}>删除</button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {notice ? <p className={styles.success} role="status">{notice}</p> : null}
    </div>
  );
}
